// Contracts stored against deal tickets, shown on the positions dashboard.
//
// submitEfpPlan files each confirmation under entity "DealTickets" with the id of the
// ticket it created, so the documents come back keyed by ticket. Joining them to the
// positions already on the dashboard turns a list of file names into something that says
// which commodity, plant and month each contract belongs to.
//
// All the documents for the entity are fetched in one call and grouped, rather than one
// request per ticket - a dashboard can show a lot of tickets.
import { AttachFile, Download } from '@mui/icons-material';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { DocumentModel, PositionModel } from '../../../../Generated/Commodity-Management-Api';
import { EFP_DOCUMENT_ENTITY } from './EfpSubmitter';

export interface EfpContractsPanelProps {
    /** Positions on the dashboard, used to describe each contract's deal ticket. */
    positions: PositionModel[];
}

interface ContractRow {
    document: DocumentModel;
    dealTicketNumber?: string | null;
    commodityName?: string;
    locationName?: string;
    deliveryLabel?: string;
    positionCount: number;
}

export default function EfpContractsPanel(props: EfpContractsPanelProps) {
    const { positions } = props;

    const [documents, setDocuments] = useState<DocumentModel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        CommodityManagementApi.listDocuments(null, EFP_DOCUMENT_ENTITY)
            .then(response => {
                if (!cancelled) setDocuments(response.data.rows ?? []);
            })
            .catch(caught => {
                if (!cancelled) setError((caught as Error)?.message ?? 'Could not load contracts');
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => { cancelled = true; };
    }, []);

    // Deal ticket id -> the positions it wrote, so a contract can be described by what it
    // actually did rather than by its file name.
    const positionsByDealTicket = useMemo(() => {
        const byTicket = new Map<number, PositionModel[]>();
        for (const position of positions) {
            if (position.dealTicketId === undefined || position.dealTicketId === null) continue;
            const group = byTicket.get(position.dealTicketId);
            if (group) group.push(position);
            else byTicket.set(position.dealTicketId, [position]);
        }
        return byTicket;
    }, [positions]);

    const rows = useMemo<ContractRow[]>(() => documents.map(document => {
        const linked = document.entityId ? positionsByDealTicket.get(document.entityId) ?? [] : [];
        const first = linked[0];

        return {
            document,
            dealTicketNumber: first?.dealTicketNumber,
            commodityName: first?.commodity?.commodityName,
            locationName: first?.location?.locationName,
            deliveryLabel: first?.budgetDate
                ? `${first.budgetDate.month}/${first.budgetDate.year}`
                : undefined,
            positionCount: linked.length,
        };
    }), [documents, positionsByDealTicket]);

    async function download(documentUrl: string) {
        setDownloading(documentUrl);
        try {
            const response = await CommodityManagementApi.downloadDocument(null, documentUrl);
            const link = document.createElement('a');
            link.href = response.data.fileUrl!;
            link.download = documentUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (caught) {
            setError((caught as Error)?.message ?? 'Could not download that contract');
        } finally {
            setDownloading(null);
        }
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Loading contracts...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AttachFile fontSize="small" />
                <Typography variant="h6" sx={{ m: 0 }}>Contracts</Typography>
                <Chip size="small" variant="outlined" label={`${rows.length}`} />
            </Box>

            {error && <Alert severity="error" variant="outlined" sx={{ mb: 1 }}>{error}</Alert>}

            {rows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No contracts stored yet. Confirmations uploaded through Upload Contract or the
                    assistant are kept here against the positions they created.
                </Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Contract</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Commodity</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Plant</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Delivery</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">Positions</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">File</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(row => (
                            <TableRow key={row.document.documentId} hover>
                                <TableCell>
                                    <Typography variant="body2">
                                        {row.dealTicketNumber || row.document.description || 'EFP contract'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {row.document.documentUrl}
                                    </Typography>
                                </TableCell>
                                <TableCell>{row.commodityName ?? '-'}</TableCell>
                                <TableCell>{row.locationName ?? '-'}</TableCell>
                                <TableCell>{row.deliveryLabel ?? '-'}</TableCell>
                                <TableCell align="right">
                                    {/* Zero means the ticket's positions are outside the
                                        dashboard's current filter, not that none exist. */}
                                    {row.positionCount > 0 ? row.positionCount : '-'}
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Download the contract">
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={downloading === row.document.documentUrl}
                                                onClick={() => download(row.document.documentUrl)}
                                            >
                                                {downloading === row.document.documentUrl
                                                    ? <CircularProgress size={16} />
                                                    : <Download fontSize="small" />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
}
